import { Button } from "@/components/ui/button"
import {
  AlertTriangle,
  CheckCircle,
  Loader2,
  Shield,
  Terminal,
  Wifi,
  X,
} from "lucide-react"
import React from "react"
import type { PaymentStatus } from "../types"

interface PaymentStatusMessageProps {
  paymentStatus: PaymentStatus
  errorMessage: string
  amount: number
  paymentIntentId: string | null
  isCancelling: boolean
  createPaymentIntentPending: boolean
  createPaymentIntentError?: Error | null
  onCancel: () => void
}

export const PaymentStatusMessage: React.FC<PaymentStatusMessageProps> = ({
  paymentStatus,
  errorMessage,
  amount,
  paymentIntentId,
  isCancelling,
  createPaymentIntentPending,
  createPaymentIntentError,
  onCancel,
}) => {
  const getStatusMessage = () => {
    switch (paymentStatus) {
      case "initializing":
        return "Initialisation de Stripe Terminal..."
      case "discovering":
        return "Recherche de lecteurs disponibles..."
      case "connecting":
        return "Connexion au lecteur..."
      case "preparing_terminal":
        return "Préparation du terminal et création du PaymentIntent..."
      case "waiting_for_card":
        return "Terminal prêt - En attente de la carte"
      case "processing":
        return "Carte détectée - Traitement en cours"
      case "requires_action":
        return "Action requise du client"
      case "succeeded":
        return "Paiement confirmé"
      case "failed":
        return "Paiement échoué"
      case "cancelling":
        return "Annulation en cours..."
      default:
        return "Paiement par carte bancaire"
    }
  }

  // État initial
  if (paymentStatus === "idle") {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">
              Paiement sécurisé par Stripe Terminal
            </h4>
            <p className="text-sm text-blue-700">
              Toutes les transactions sont chiffrées et sécurisées. Cliquez sur
              "Démarrer le paiement" pour commencer.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // États de préparation
  if (
    paymentStatus === "initializing" ||
    paymentStatus === "discovering" ||
    paymentStatus === "connecting" ||
    paymentStatus === "preparing_terminal"
  ) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Wifi className="h-5 w-5 text-blue-600 animate-pulse" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 mb-1">
              {getStatusMessage()}
            </h4>
            <p className="text-sm text-blue-700">Veuillez patienter...</p>
            {createPaymentIntentPending && (
              <p className="text-xs text-blue-600 mt-1">
                Création du PaymentIntent en cours...
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Annuler
          </Button>
        </div>
        {paymentIntentId && (
          <div className="mt-3 text-xs text-blue-600 font-mono">
            PaymentIntent: {paymentIntentId}
          </div>
        )}
      </div>
    )
  }

  // Attente de la carte
  if (paymentStatus === "waiting_for_card") {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Terminal className="h-5 w-5 text-orange-600 animate-bounce" />
          <div className="flex-1">
            <h4 className="font-medium text-orange-900 mb-1">
              Terminal prêt - En attente de la carte
            </h4>
            <p className="text-sm text-orange-700">
              Demandez au client d'insérer ou de présenter sa carte sur le
              terminal.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Annuler
          </Button>
        </div>
        <div className="mt-3 text-center">
          <div className="text-sm text-orange-700 font-medium">
            Le terminal attend la carte du client...
          </div>
          <div className="text-xs text-orange-600 mt-1">
            Le processus continuera automatiquement une fois la carte détectée
          </div>
        </div>
      </div>
    )
  }

  // Progression du paiement
  if (paymentStatus === "processing") {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 mb-1">
              Carte détectée - Traitement en cours
            </h4>
            <p className="text-sm text-blue-700">
              Le terminal traite automatiquement la transaction...
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Annuler
          </Button>
        </div>
        {paymentIntentId && (
          <div className="mt-3 text-xs text-blue-600 font-mono">
            Transaction: {paymentIntentId}
          </div>
        )}
      </div>
    )
  }

  // Action requise
  if (paymentStatus === "requires_action") {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-yellow-600 animate-pulse" />
          <div className="flex-1">
            <h4 className="font-medium text-yellow-900 mb-1">
              Action requise du client
            </h4>
            <p className="text-sm text-yellow-700">
              Le client saisit son code PIN ou effectue une autre vérification
              sur le terminal...
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isCancelling}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Annuler
          </Button>
        </div>
        {paymentIntentId && (
          <div className="mt-3 text-xs text-yellow-600 font-mono">
            Transaction: {paymentIntentId}
          </div>
        )}
      </div>
    )
  }

  // Succès
  if (paymentStatus === "succeeded") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <div>
            <h4 className="font-medium text-green-900 mb-1">
              Paiement confirmé
            </h4>
            <p className="text-sm text-green-700">
              Le paiement de {amount.toFixed(2)}€ a été traité avec succès.
            </p>
          </div>
        </div>
        {paymentIntentId && (
          <div className="mt-3 text-xs text-green-600 font-mono">
            Transaction: {paymentIntentId}
          </div>
        )}
      </div>
    )
  }

  // Échec
  if (paymentStatus === "failed") {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900 mb-1">Paiement échoué</h4>
            <p className="text-sm text-red-700">
              {errorMessage ||
                "Une erreur est survenue lors du traitement du paiement."}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Annulation en cours
  if (paymentStatus === "cancelling") {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
          <div>
            <h4 className="font-medium text-red-900 mb-1">
              Annulation en cours...
            </h4>
            <p className="text-sm text-red-700">
              Annulation du paiement et déconnexion du terminal...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Erreurs de mutations
  if (createPaymentIntentError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900 mb-1">
              Erreur de création du PaymentIntent
            </h4>
            <p className="text-sm text-red-700">
              {createPaymentIntentError.message ||
                "Impossible de créer l'intention de paiement."}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}
